// Third party imports
import {
  LinkField,
  ImageField,
  Field,
  useSitecoreContext,
  Text,
} from '@sitecore-jss/sitecore-jss-nextjs';
import * as React from 'react';
import { useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import { useRouter } from 'next/router';
import {
  StstickyBox,
  StStickyContainer,
  StButtonsContainer,
  StCtaButtons,
  StFooterText,
  StUpArrowImg,
  StJSSLink,
  StMoveTotopLink,
  StMoveText,
} from './StickyFooter.styles';
import useStickyScroll from '../../lib/hooks/useStickyScroll';
import Box from '@mui/material/Box';
import { trackEvent } from '../../lib/utils/gtmService';
import MoveTopIcon from '../../../public/move-top.png';
import { ButtonType } from '../../lib/type/Button';
import isClinicalTrialSite from '../../lib/utils/siteHelper';
import UiText from '../../components/shared/Typography/UiText';
import ButtonPrimary from '../../components/shared/Button/PrimaryButton';
import ButtonSecondary from '../../components/shared/Button/SecondaryButton';
import ButtonDiscreet from '../../components/shared/Button/ButtonDiscreet';
import useApplyV2Theme from '../../lib/hooks/useApplyV2Theme';

interface CtaButtonItem {
  fields: {
    buttonVariant?: Field<string>;
    link: LinkField;
    image?: ImageField;
    ariaLabel?: Field<string>;
  };
}

export interface IStickyData {
  ctaAllignment: Field<string>;
  ctaButtons: Array<CtaButtonItem>;
  footerText: Field<string>;
  scrollToTopText: Field<string>;
  enableAutoShowMobile: Field<boolean>;
}

interface StickyFooterProps {
  stickyData: IStickyData;
}

const StickyFooter = (props: StickyFooterProps): JSX.Element => {
  const { sitecoreContext } = useSitecoreContext();
  const router = useRouter();
  const theme = useTheme();
  const siteName = sitecoreContext?.site?.name;
  const enableAutoShowMobile = props?.stickyData?.enableAutoShowMobile?.value;
  const { isToapplyV2Theme } = useApplyV2Theme();

  const { scrolling, scrollThreshold } = useStickyScroll(router, enableAutoShowMobile);
  const isLaptopView = useMediaQuery(theme.breakpoints.up('lg'));
  const isMobileView = useMediaQuery(theme.breakpoints.down('sm'));

  React.useEffect(() => {
    let resizeTimeout: NodeJS.Timeout;

    const positionCookieIcon = () => {
      const footerElement = document.getElementById('sticky-footer');
      const cookieIconElement = document.getElementById('ot-sdk-btn-floating');

      if (footerElement && cookieIconElement) {
        const footerRect = footerElement.getBoundingClientRect();
        const windowHeight = window.innerHeight;

        if (footerRect.top < windowHeight) {
          const distanceFromBottom = windowHeight - footerRect.top;
          cookieIconElement.style.setProperty(
            'bottom',
            `${distanceFromBottom + 14}px`,
            'important'
          );
        } else {
          cookieIconElement.style.removeProperty('bottom');
        }
      }
    };

    const handleScroll = () => {
      positionCookieIcon();
    };

    const handleResize = () => {
      clearTimeout(resizeTimeout);
      resizeTimeout = setTimeout(() => {
        positionCookieIcon();
      }, 100);
    };

    positionCookieIcon();

    window.addEventListener('resize', handleResize);
    window.addEventListener('load', positionCookieIcon);
    window.addEventListener('scroll', handleScroll);

    return () => {
      window.removeEventListener('resize', handleResize);
      window.removeEventListener('load', positionCookieIcon);
      window.removeEventListener('scroll', handleScroll);
      clearTimeout(resizeTimeout);
    };
  }, []);

  if (!props?.stickyData) return <></>;
  const { stickyData } = props;
  const { ctaAllignment, footerText, ctaButtons, scrollToTopText } = stickyData;

  const moveToTop = () => {
    window.scrollTo(0, 0);
  };

  const scrollToSection = (e: any, item: any) => {
    e.preventDefault();
    trackEvent(
      {
        event: 'Sticky CTA Clicked',
      },
      isClinicalTrialSite(siteName as string) ? true : false
    );
    try {
      if (item) {
        const itemId = item?.fields?.link?.value?.anchor;
        const targetElement = document.getElementById(itemId);
        if (targetElement) {
          window.scroll({
            top: targetElement.offsetTop,
            left: 0,
            behavior: 'smooth',
          });
        }
      }
    } catch (_error) {}
  };

  const alignment = ctaAllignment?.value;
  const numberOfCTA = ctaButtons?.length;

  const renderFooterText = () => {
    if (
      (alignment === 'Right' && isLaptopView && numberOfCTA >= 1) ||
      (alignment === 'Right' && !isLaptopView && numberOfCTA === 1)
    ) {
      return (
        <StFooterText>
          {isToapplyV2Theme ? (
            <UiText styles={{ fontSize: '26px' }}>{footerText?.value}</UiText>
          ) : (
            <Text field={footerText} />
          )}
        </StFooterText>
      );
    }
    return null;
  };

  const buttonAlignment = () => {
    if (
      (alignment === 'Right' && isLaptopView && numberOfCTA >= 1) ||
      (alignment === 'Right' && !isLaptopView && numberOfCTA === 1)
    ) {
      return 'space-between';
    } else if (alignment === 'Right' && !isLaptopView && numberOfCTA === 2) {
      return 'center';
    } else {
      return 'center';
    }
  };

  if (!scrolling) {
    return <></>;
  }

  return (
    <StstickyBox id="sticky-footer" data-testid="stickyFooter">
      <StStickyContainer data-testid="stickyContainer">
        {((alignment === 'Right' && !isLaptopView && numberOfCTA > 1) ||
          alignment === 'Center') && <Box sx={{ display: 'flex', width: '107.6px' }}></Box>}

        <StButtonsContainer data-testid="first" sx={{ justifyContent: buttonAlignment() }}>
          {renderFooterText()}
          <StCtaButtons data-testid="second" alignment={alignment}>
            {ctaButtons?.map((button, i) => {
              const variant = button?.fields?.buttonVariant?.value;

              if (variant === ButtonType.CONTAINED) {
                return (
                  <Box
                    key={i}
                    onClick={(e) => scrollToSection(e, button)}
                    sx={{ ...(isMobileView && { width: '100%' }) }}
                  >
                    {isToapplyV2Theme ? (
                      <ButtonPrimary width={isMobileView ? '100%' : '175px'}>
                        {button?.fields?.link?.value?.text}
                      </ButtonPrimary>
                    ) : (
                      <StJSSLink
                        className={'sticky-footer-btn btn'}
                        variant={button?.fields?.buttonVariant?.value}
                        aria-label={button?.fields?.ariaLabel?.value}
                        field={button?.fields?.link}
                        alignment={alignment}
                        numberOfCTA={numberOfCTA}
                      />
                    )}
                  </Box>
                );
              }

              if (variant === ButtonType.OUTLINED) {
                return isToapplyV2Theme ? (
                  <Box
                    key={i}
                    onClick={(e) => scrollToSection(e, button)}
                    sx={{ ...(isMobileView && { width: '100%' }) }}
                  >
                    <ButtonSecondary width={isMobileView ? '100%' : '175px'}>
                      {button?.fields?.link?.value?.text}
                    </ButtonSecondary>
                  </Box>
                ) : (
                  <StJSSLink
                    className={'sticky-footer-btn btn'}
                    key={i}
                    variant={button?.fields?.buttonVariant?.value}
                    aria-label={button?.fields?.ariaLabel?.value}
                    field={button?.fields?.link}
                    alignment={alignment}
                    numberOfCTA={numberOfCTA}
                  />
                );
              }

              // fallback to JSSLink
              return (
                <StJSSLink
                  className="sticky-footer-btn btn"
                  key={i}
                  variant={variant}
                  aria-label={button?.fields?.ariaLabel?.value}
                  field={button?.fields?.link}
                  alignment={alignment}
                  numberOfCTA={numberOfCTA}
                />
              );
            })}
          </StCtaButtons>
        </StButtonsContainer>

        {scrollThreshold &&
          (isToapplyV2Theme ? (
            <ButtonDiscreet
              width={isMobileView ? '100%' : '200px'}
              onClick={moveToTop}
              data-testid="moveToTop"
              iconLeft={
                <StUpArrowImg
                  alt="up-arrow"
                  src={MoveTopIcon?.src || MoveTopIcon}
                  height={8}
                  width={8}
                />
              }
            >
              {scrollToTopText?.value}
            </ButtonDiscreet>
          ) : (
            <StMoveTotopLink onClick={moveToTop} data-testid="moveToTop">
              <StUpArrowImg
                alt="up-arrow"
                src={MoveTopIcon?.src || MoveTopIcon}
                height={8}
                width={8}
              />
              <StMoveText>{scrollToTopText?.value}</StMoveText>
            </StMoveTotopLink>
          ))}
      </StStickyContainer>
    </StstickyBox>
  );
};

export default StickyFooter;
